import type { OfferInfo } from '@patrianna-payments/shared-store/payments/types/openApiModels/OfferInfo'
import { createSelector } from '@reduxjs/toolkit'

import type { ReduxState } from 'src/store/types'

import { sweepstakeEnabledSelector } from '../appConfig/selectors'

import {
  getHighestPriorityMostExpensiveSpecialOffer,
  getRegularOffersForQuickPurchase,
  getSpecialOffersForQuickPurchase,
  hasSpecialOffers,
} from './utils'

const ALLOWED_SHOP_OFFERS_TYPES = [
  'one_time',
  'permanent',
  'personalized',
  'daily',
  'weekly',
  'fixed_daily',
  'fixed_weekly',
  'reward',
]
const OFFER_TYPES = ['one_time', 'permanent', 'daily', 'weekly', 'fixed_daily', 'fixed_weekly', 'reward']

const getShop = (state: ReduxState) => state.shop

const checkOfferType = (el: OfferInfo) => {
  return Object.prototype.hasOwnProperty.call(el, 'offerType')
}

export const getOffersSelector = createSelector(getShop, (shop) => shop.offers)
export const getAppliedOffersSelector = createSelector(getShop, (shop) => shop.applied)
export const purchaseLimitsSelector = createSelector(getShop, (shop) => shop.limits)
export const offersLoadedSelector = createSelector(getShop, (shop) => shop.loaded)

export const getIsUserOffersSelector = createSelector(getShop, (shop) => shop.isUserOffers)

export const getVipOffersSelector = createSelector(getOffersSelector, (offers) => {
  return offers?.filter((it: OfferInfo) => {
    if (checkOfferType(it)) {
      return it.offerType.toLowerCase() === 'subscription'
    } else {
      return it
    }
  })
})

export const getShopOffersSelector = createSelector(getOffersSelector, (offers) => {
  return offers?.filter((it) => it?.offerType && OFFER_TYPES.includes(it.offerType.toLowerCase()))
})

export const getOfferByCodeSelector = createSelector(
  getShopOffersSelector,
  (_: ReduxState, code: string) => code,
  (offers, _code) => {
    return offers?.find((i) => i.code === _code)
  },
)

export const getIsOfferAvailableSelector = createSelector(
  getShopOffersSelector,
  (state: ReduxState, code: string) => getOfferByCodeSelector(state, code),
  (offers, offer) => offers?.length === 0 || (offers?.length > 0 && Boolean(offer)),
)

export const getOffersBySpecificOfferField = (field: keyof OfferInfo) =>
  createSelector(
    getShopOffersSelector,
    () => field,
    (offers, _field) => {
      // @ts-ignore
      return offers?.filter((i) => Boolean(i?.[_field]))
    },
  )

export const getAllSCDiscountOffersSelector = createSelector(
  getShopOffersSelector,
  getAppliedOffersSelector,
  (offers, appliedOffers) => {
    const appliedOfferCodes = appliedOffers ? appliedOffers.map((i) => i.code) : []
    const scFirstTimeOffers =
      offers?.filter((i) => {
        return i.sweepstakeFirstOffer !== 0 && i.sweepstakeFirstOffer !== i.sweepstakeMoney
      }) || []

    return scFirstTimeOffers.filter((el) => !appliedOfferCodes.includes(el.code))
  },
)

export const getAllSCDiscountOffersForModalsSelector = createSelector(
  getOffersSelector,
  getAppliedOffersSelector,
  (offers, appliedOffers) => {
    const appliedOfferCodes = appliedOffers ? appliedOffers.map((i) => i.code) : []
    const scFirstTimeOffers =
      offers?.filter((i) => {
        return i.sweepstakeFirstOffer !== 0 && i.sweepstakeFirstOffer !== i.sweepstakeMoney
      }) || []

    return scFirstTimeOffers.filter((el) => !appliedOfferCodes.includes(el.code))
  },
)

export const getModalOffersSelector = createSelector(getAllSCDiscountOffersForModalsSelector, (discountOffers) => {
  return discountOffers.filter((i) => Boolean(i.popUpImageUrl))
})

export const getBannerOffersSelector = createSelector(getAllSCDiscountOffersSelector, (discountOffers) => {
  return discountOffers.filter((i) => Boolean(i.bannerImageUrl))
})

export const discountSCOfferAvailableSelector = createSelector(getAllSCDiscountOffersSelector, (discountOffers) => {
  return discountOffers?.length > 0
})

export const offersWereAppliedSelector = createSelector(
  getAppliedOffersSelector,
  (appliedOffers) => appliedOffers?.length > 0,
)

export const isDiscountOffersAvailable = createSelector(
  sweepstakeEnabledSelector,
  offersWereAppliedSelector,
  discountSCOfferAvailableSelector,
  (scEnabled, appliedOffers, scDiscountApplied) => {
    if (scEnabled) {
      return scDiscountApplied
    }

    return !appliedOffers
  },
)

export const getMaxPriorityOrRandomOfferSelector = (getterFunction: (state: ReduxState) => OfferInfo[]) =>
  createSelector(getterFunction, (offers): OfferInfo | null => {
    if (offers?.length) {
      const maxPriority = offers.reduce((acc: OfferInfo, curr: OfferInfo) =>
        acc.priority < curr.priority ? acc : curr,
      )
      const maxPriorityOffers = offers.filter((offer: OfferInfo) => offer.priority === maxPriority.priority)

      return maxPriorityOffers[Math.floor(Math.random() * maxPriorityOffers.length)]
    }

    return null
  })

// @TODO will use getMaxPriorityOrRandomOfferSelector instead of getMaxPriorityOrRandomModalOfferSelector!

export const getMaxPriorityOfferFromOffersSelector = createSelector(
  (state: ReduxState, offers: OfferInfo[]) => offers,
  (offers): OfferInfo | null => {
    if (!offers.length) {
      return null
    }
    const maxPriority = offers.reduce((acc: OfferInfo, curr: OfferInfo) => (acc.priority < curr.priority ? acc : curr))
    const maxPriorityOffers = offers.filter((offer: OfferInfo) => offer.priority === maxPriority.priority)

    return maxPriorityOffers[Math.floor(Math.random() * maxPriorityOffers.length)]
  },
)

export const getMaxPriorityOrRandomModalOfferSelector = createSelector(
  (state: ReduxState) => getMaxPriorityOfferFromOffersSelector(state, getModalOffersSelector(state)),
  (maxPriorityOffer): OfferInfo | null => maxPriorityOffer,
)

export const getSpecialOffersSelectorByTag = createSelector(
  (state: ReduxState, offers: OfferInfo[]) => offers,
  (offers): OfferInfo[] | null => offers?.filter((off) => off.tags?.includes('special_offer')),
)

export const getSpecialOffers = createSelector(
  getAllSCDiscountOffersSelector,
  (state: ReduxState) => state,
  (offers, state) => getSpecialOffersSelectorByTag(state, offers),
)

export const isOffersAvailableSelector = createSelector(
  getModalOffersSelector,
  isDiscountOffersAvailable,
  sweepstakeEnabledSelector,
  (SCModalOffers, isFirstPurchaseNotUsed, sweepstakeEnabled) => {
    return sweepstakeEnabled && SCModalOffers.length > 0 && isFirstPurchaseNotUsed
  },
)

export const getOffersByTagSelector = createSelector(
  getShopOffersSelector,
  (_state: ReduxState, tag: string) => tag,
  (offers, tag): OfferInfo[] | null => {
    return offers?.filter(({ tags }) => tags?.join().toLowerCase().includes(tag?.toLowerCase())) || null
  },
)

export const getCheckedShopOffersSelector = createSelector(getOffersSelector, (offers) => {
  return offers?.filter((it) => {
    if (checkOfferType(it)) {
      return ALLOWED_SHOP_OFFERS_TYPES.includes(it.offerType.toLowerCase())
    } else {
      return true
    }
  })
})

export const getMaxPriorityOffersFromOffersSelector = createSelector(
  (_state: ReduxState, offers: OfferInfo[]) => offers,
  (offers): OfferInfo[] | null => {
    if (!offers?.length) {
      return null
    }

    const maxPriority = offers.reduce((acc: OfferInfo, curr: OfferInfo) => (acc.priority < curr.priority ? acc : curr))

    return offers.filter((offer: OfferInfo) => offer.priority === maxPriority.priority)
  },
)

export const getNoSpecialOffersSelectorByTag = createSelector(
  (_state: ReduxState, offers: OfferInfo[]) => offers,
  (offers): OfferInfo[] | null => offers?.filter((off) => !off.tags?.includes('special_offer')),
)

export const getWholeNoSpecialOffersSortedByPriceAscSelector = createSelector(
  (state: ReduxState) => getNoSpecialOffersSelectorByTag(state, getSortedOffersByAsc(state)),
  (offers): OfferInfo[] | null => offers,
)

export const getWholeSpecialOffersSelector = createSelector(
  (state: ReduxState) => getSpecialOffersSelectorByTag(state, getOffersSelector(state)),
  (offers): OfferInfo[] | null => offers,
)

export const getLastPurchasedOfferSelector = createSelector(getShop, (shop) => shop.lastPurchasedOffer)

export const getSortedOffersByAsc = createSelector(getCheckedShopOffersSelector, (offers) =>
  [...offers].sort((a, b) => (a.price > b.price ? 1 : -1)),
)

export const getMaxPriorityFirstPurchaseOfferSelector = createSelector(
  (state: ReduxState) => getMaxPriorityOffersFromOffersSelector(state, getOffersByTagSelector(state, '1st purchase')),
  (maxPriorityOffers): OfferInfo | null => (maxPriorityOffers?.length ? maxPriorityOffers[0] : null),
)

export const getUpdatedSpecialOffers = createSelector(
  getSortedOffersByAsc,
  (state: ReduxState) => state,
  (offers, state) => getSpecialOffersSelectorByTag(state, offers),
)

/*
 * 1. [1 offer] - first_purchase offer or offer with the closest price of the last_purchase offer
 * 2.a [2,n offers] - special_offer offers sorted by max priority and highest price (skip [1 offer])
 * 2.b [2,n offers] - if special_offer doest exist, take no_special_offer offers with the closest price of [1 offer] (skip [1 offer])
 * */
export const getQuickPurchaseOffersSelector = createSelector(
  (_state: ReduxState, size?: number) => size,
  getMaxPriorityFirstPurchaseOfferSelector,
  getLastPurchasedOfferSelector,
  offersWereAppliedSelector,
  getSortedOffersByAsc,
  getWholeNoSpecialOffersSortedByPriceAscSelector,
  getShopOffersSelector,
  (
    size,
    firstPurchaseOffer,
    lastPurchaseOffer,
    offersWereApplied,
    sortedOffersByAsc,
    noSpecialOffersSortedByPrice = [],
    shopOffers,
  ): OfferInfo[] | null => {
    const isFirstOfferExisted = !offersWereApplied && firstPurchaseOffer
    const offerForSorting = isFirstOfferExisted ? firstPurchaseOffer : lastPurchaseOffer

    const getFilteredOffers = (offers: OfferInfo[], offerToCondition: OfferInfo): OfferInfo[] => {
      if (!offerToCondition?.code) {
        return []
      }

      const getIsOfferNoFirstOfferAndWithClosestPrice = ({ code, price }: OfferInfo) => {
        return offerToCondition?.code !== code && Number(offerToCondition?.price) < price
      }

      return offers?.filter(getIsOfferNoFirstOfferAndWithClosestPrice) || []
    }

    // [1 offer] --- start
    const [offerWithClosestPrice] = getFilteredOffers(sortedOffersByAsc, offerForSorting)
    const firstOffer = isFirstOfferExisted ? firstPurchaseOffer : offerWithClosestPrice
    // [1 offer] --- end

    // [2,n offers] --- start
    // need skip last_purchase offer and [1 offer]
    const secondOffer = getHighestPriorityMostExpensiveSpecialOffer(shopOffers, [
      offerForSorting?.code,
      firstOffer?.code,
    ])

    // need skip last_purchase offer and [1 offer]
    const noSpecialOffersFiltered = getFilteredOffers(
      getFilteredOffers(noSpecialOffersSortedByPrice, offerForSorting),
      firstOffer,
    )
    // [2,n offers] --- end

    const offers: OfferInfo[] = [firstOffer, secondOffer, ...noSpecialOffersFiltered].filter(Boolean)

    return size ? offers.slice(0, size) : offers
  },
)

export const getTwoLowerCostOfferSelector = (code: string) =>
  createSelector(
    getSortedOffersByAsc,
    () => code,
    (offers, _code) => {
      const offerIndex = offers?.findIndex((i) => i.code === _code)
      const startIndex = offerIndex - 2 >= 0 ? offerIndex - 2 : 0

      return offers.slice(startIndex, offerIndex)
    },
  )

export const getQuickPurchaseFallbackSelector = createSelector(
  getOffersSelector,
  getLastPurchasedOfferSelector,
  (offers: OfferInfo[], lastPurchasedOffer: OfferInfo) => {
    if (hasSpecialOffers(offers)) {
      return getSpecialOffersForQuickPurchase(offers, lastPurchasedOffer)
    } else {
      return getRegularOffersForQuickPurchase(offers, lastPurchasedOffer)
    }
  },
)
